import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Store pickup: the customer collects the order. Free, no carrier and no Envía request. */
const AdminPickupShipping = () => {
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { axios.get(`${API}/shipping/config/pickup`, { headers }).then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { enabled, all_countries, countries, name, note, instructions, note_en, instructions_en } = cfg;
      const r = await axios.put(`${API}/shipping/config/pickup`, { enabled, all_countries, countries, name, note, instructions, note_en, instructions_en }, { headers });
      setCfg(r.data);
      toast({ title: 'Saved', description: 'Store pickup settings updated' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  const toggleCountry = (code) => setCfg({
    ...cfg,
    countries: cfg.countries.includes(code) ? cfg.countries.filter((c) => c !== code) : [...cfg.countries, code],
  });

  return (
    <Card data-testid="pickup-settings">
      <CardHeader>
        <CardTitle>Store pickup</CardTitle>
        <CardDescription>
          Lets the customer collect the order instead of having it shipped. Always free. The order is saved with the
          pickup option and nothing is sent to Envía: no label, no carrier pickup. Mark the order as delivered when the
          customer collects it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between border rounded-md p-3">
          <div>
            <Label>Offer store pickup at checkout</Label>
            <p className="text-sm text-gray-500">It appears after the delivery options, in the countries chosen below.</p>
          </div>
          <Switch data-testid="pickup-toggle" checked={!!cfg.enabled} onCheckedChange={(on) => setCfg({ ...cfg, enabled: on })} />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Offer it in every country</Label>
              <p className="text-sm text-gray-500">Anyone, anywhere, can choose to come and collect their order at the warehouse.</p>
            </div>
            <Switch data-testid="pickup-all-countries" checked={!!cfg.all_countries} onCheckedChange={(on) => setCfg({ ...cfg, all_countries: on })} />
          </div>
          {!cfg.all_countries && (
            <div className="space-y-2">
              <Label>Only in these countries</Label>
              <div className="flex flex-wrap gap-2" data-testid="pickup-countries">
                {(cfg.available_countries || []).map((c) => (
                  <button key={c.code} type="button" onClick={() => toggleCountry(c.code)}
                          className={`px-3 py-1.5 text-sm rounded border ${cfg.countries.includes(c.code) ? 'bg-[#be1f1a] text-white border-[#be1f1a]' : 'bg-white text-gray-700 border-gray-300'}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="pickup-name">Name (optional)</Label>
            <Input id="pickup-name" data-testid="pickup-name" maxLength={60} placeholder="Recoger en tienda / Store pickup"
                   value={cfg.name || ''} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} />
            <p className="text-xs text-gray-500">Empty = a name in the customer&apos;s language.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pickup-note">Line shown at checkout (Spanish)</Label>
            <Input id="pickup-note" data-testid="pickup-note" maxLength={200} placeholder="Sabaneta, Antioquia"
                   value={cfg.note || ''} onChange={(e) => setCfg({ ...cfg, note: e.target.value })} />
            <p className="text-xs text-gray-500">Shown under the option before paying. Keep the exact address out of it if you prefer.</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pickup-instructions">Pickup details for the customer (Spanish)</Label>
          <Textarea id="pickup-instructions" data-testid="pickup-instructions" rows={4} maxLength={1000}
                    placeholder={'Address, hours, who to ask for…'}
                    value={cfg.instructions || ''} onChange={(e) => setCfg({ ...cfg, instructions: e.target.value })} />
          <p className="text-xs text-gray-500">
            Shown in the customer&apos;s order e-mails in place of a delivery address, one line per row. There is no
            automatic &quot;ready for pickup&quot; e-mail: contact the customer when the order is ready.
          </p>
        </div>

        <div className="rounded-md border p-4 space-y-4 bg-gray-50" data-testid="pickup-english">
          <div>
            <Label className="text-base">English version</Label>
            <p className="text-xs text-gray-500">
              Shown to customers who don&apos;t use Spanish. If you leave these empty they get the Spanish text above.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pickup-note-en">Line shown at checkout (English)</Label>
            <Input id="pickup-note-en" data-testid="pickup-note-en" maxLength={200} placeholder="Sabaneta, Antioquia, Colombia"
                   value={cfg.note_en || ''} onChange={(e) => setCfg({ ...cfg, note_en: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pickup-instructions-en">Pickup details for the customer (English)</Label>
            <Textarea id="pickup-instructions-en" data-testid="pickup-instructions-en" rows={4} maxLength={1000}
                      placeholder={'Address, hours, who to ask for…'}
                      value={cfg.instructions_en || ''} onChange={(e) => setCfg({ ...cfg, instructions_en: e.target.value })} />
          </div>
        </div>

        <Button data-testid="pickup-save" onClick={save} disabled={saving} className="bg-[#be1f1a] hover:bg-[#a71b17]">
          {saving ? 'Saving…' : 'Save store pickup'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminPickupShipping;
